<template>
	<div class="container-fluid p-4">
		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<!-- Enablement (§31 Rule 1, M10): which catalog trap types the district
		     fields. The create path already refuses a type the district has not
		     enabled; this is that rule's management surface. -->
		<div class="card mb-4">
			<div class="card-body">
				<div
					class="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3"
				>
					<div>
						<h2 class="h5 mb-0">Enabled trap types</h2>
						<p class="text-muted mb-0 small">
							The catalog types your district fields. Most districts have a
							handful; add one when the field programme starts using it.
						</p>
					</div>
					<button
						class="btn btn-primary"
						type="button"
						@click="openAddTrapType"
					>
						<i class="bi bi-plus-lg me-1"></i>Add trap type
					</button>
				</div>
				<div v-if="catalogLoading" class="text-muted">Loading…</div>
				<div
					v-else-if="enabledCatalog.length === 0"
					class="text-center py-4 text-muted"
				>
					No trap types are enabled yet. Add one to make it available for field
					use.
				</div>
				<table v-else class="table align-middle mb-0">
					<thead>
						<tr>
							<th>Trap type</th>
							<th>Short code</th>
							<th>Make / model</th>
							<th>State</th>
							<th class="text-end">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="entry in enabledCatalog" :key="entry.trap_type_id">
							<td>{{ entry.name }}</td>
							<td>
								<code>{{ entry.acronym || "—" }}</code>
							</td>
							<td>
								{{
									[entry.manufacturer, entry.model]
										.filter(Boolean)
										.join(" — ") || "—"
								}}
							</td>
							<td>
								<span v-if="entry.retired" class="badge text-bg-secondary">
									Retired
								</span>
								<span v-else class="badge text-bg-success">Enabled</span>
							</td>
							<td class="text-end">
								<button
									class="btn btn-sm btn-outline-secondary"
									type="button"
									:disabled="savingEnablement === entry.trap_type_id"
									@click="toggleEnablement(entry, false)"
								>
									Remove
								</button>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<!-- Picking a catalog type is a search, not a 40-row scan: most districts
		     field five or fewer, so the enabled list above is the page and this
		     modal is how one more joins it. -->
		<div
			v-if="addTrapTypeOpen"
			class="modal fade show d-block"
			tabindex="-1"
			role="dialog"
			aria-modal="true"
			aria-labelledby="addTrapTypeTitle"
			@keydown.esc="closeAddTrapType"
		>
			<div class="modal-dialog modal-lg modal-dialog-scrollable">
				<div class="modal-content">
					<div class="modal-header">
						<h2 id="addTrapTypeTitle" class="modal-title h5 mb-0">
							Add a trap type
						</h2>
						<button
							type="button"
							class="btn-close"
							aria-label="Close"
							@click="closeAddTrapType"
						></button>
					</div>
					<div class="modal-body">
						<div v-if="error" class="alert alert-danger" role="alert">
							{{ error }}
						</div>
						<label class="form-label" for="trapTypeSearch">
							Search the catalog
						</label>
						<input
							id="trapTypeSearch"
							v-model="trapTypeSearch"
							type="search"
							class="form-control mb-3"
							placeholder="Name, make, model, or description"
							autofocus
						/>
						<div
							v-if="availableCatalog.length === 0"
							class="text-center py-3 text-muted"
						>
							{{
								trapTypeSearch
									? "No catalog types match that search."
									: "Every available catalog type is already enabled."
							}}
						</div>
						<table v-else class="table align-middle mb-0">
							<thead>
								<tr>
									<th>Trap type</th>
									<th>Make / model</th>
									<th class="text-end"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="entry in availableCatalog" :key="entry.trap_type_id">
									<td>
										<div class="fw-semibold">{{ entry.name }}</div>
										<div
											v-if="
												entry.acronym ||
												entry.target_species ||
												entry.provenance
											"
											class="text-muted small"
										>
											<code v-if="entry.acronym" class="me-2">{{
												entry.acronym
											}}</code>
											{{ entry.target_species || entry.provenance }}
										</div>
									</td>
									<td>
										{{
											[entry.manufacturer, entry.model]
												.filter(Boolean)
												.join(" — ") || "—"
										}}
									</td>
									<td class="text-end">
										<ButtonLoading
											:loading="savingEnablement === entry.trap_type_id"
											class="btn btn-sm btn-primary"
											type="button"
											@click="addTrapType(entry)"
										>
											Add
										</ButtonLoading>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div class="modal-footer">
						<button
							type="button"
							class="btn btn-outline-secondary"
							@click="closeAddTrapType"
						>
							Done
						</button>
					</div>
				</div>
			</div>
		</div>
		<div v-if="addTrapTypeOpen" class="modal-backdrop fade show"></div>

		<div class="card mb-4">
			<div class="card-body">
				<h2 class="h5 mb-0">
					{{
						editingID === null ? "New Configuration" : "Supersede Configuration"
					}}
				</h2>
				<p class="text-muted mb-3 small">
					A correction is a new version, never an edit: existing instances keep
					the version they were set with.
				</p>

				<form class="row g-3" style="max-width: 720px" @submit.prevent="submit">
					<div class="col-12">
						<label class="form-label" for="configName">Name</label>
						<input
							id="configName"
							v-model="form.name"
							type="text"
							class="form-control"
							:disabled="editingID !== null"
							required
						/>
					</div>
					<div class="col-12">
						<label class="form-label" for="configTrapType">Trap type</label>
						<select
							id="configTrapType"
							v-model="form.trap_type_id"
							class="form-select"
							required
						>
							<option :value="0" disabled>
								{{
									trapTypeChoices.length === 0
										? "Add and enable a trap type above first"
										: "Choose a trap type"
								}}
							</option>
							<option
								v-for="entry in trapTypeChoices"
								:key="entry.id"
								:value="entry.id"
							>
								{{ trapTypeLabel(entry) }}
							</option>
						</select>
					</div>
					<div class="col-12">
						<label class="form-label" for="configLures">Lures</label>
						<select
							id="configLures"
							v-model="form.lures"
							class="form-select"
							multiple
							size="5"
						>
							<option
								v-for="entry in vocabulary.lure"
								:key="entry.id"
								:value="entry.id"
							>
								{{ entry.name }}
							</option>
						</select>
						<div class="form-text">
							Hold Ctrl/⌘ to select more than one; none for a setup with no
							lure.
						</div>
					</div>
					<div class="col-12">
						<label class="form-label" for="configInstructions"
							>Instructions</label
						>
						<textarea
							id="configInstructions"
							v-model="form.setup_instructions"
							class="form-control"
							rows="3"
							placeholder="Optional field notes"
						/>
					</div>
					<div class="col-12 d-flex gap-2">
						<ButtonLoading
							:loading="saving"
							class="btn btn-primary"
							type="submit"
						>
							{{ editingID === null ? "Create" : "Supersede" }}
						</ButtonLoading>
						<button
							v-if="editingID !== null"
							type="button"
							class="btn btn-outline-secondary"
							@click="resetForm"
						>
							Cancel
						</button>
					</div>
				</form>
			</div>
		</div>

		<div class="card">
			<div class="card-body">
				<h2 class="h5 mb-3">The setup library</h2>
				<div v-if="loading" class="text-muted">Loading…</div>
				<div v-else-if="configurations.length === 0" class="text-muted">
					No configurations yet.
				</div>
				<table v-else class="table align-middle mb-0">
					<thead>
						<tr>
							<th>Name</th>
							<th>Version</th>
							<th>Trap type</th>
							<th>Lures</th>
							<th>State</th>
							<th class="text-end">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="config in configurations" :key="config.id">
							<td>{{ config.name }}</td>
							<td>v{{ config.version }}</td>
							<td>{{ typeName(config.trap_type_id) }}</td>
							<td>{{ lureNames(config.lures).join(", ") || "—" }}</td>
							<td>
								<span v-if="config.retired" class="badge text-bg-secondary">
									Retired
								</span>
								<span v-else class="badge text-bg-success">Active</span>
							</td>
							<td class="text-end">
								<button
									class="btn btn-sm btn-outline-primary me-1"
									@click="beginSupersede(config)"
								>
									Supersede
								</button>
								<button
									v-if="!config.retired"
									class="btn btn-sm btn-outline-secondary"
									@click="retire(config.id)"
								>
									Retire
								</button>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<ToastNotification
			v-if="toastShow"
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type {
	MapReviewTrapVocabulary,
	MapReviewVocabularyEntry,
	TrapConfigurationResult,
	TrapConfigurationWrite,
	TrapTypeCatalogEntry,
	TrapTypeCatalogResponse,
} from "@/type/api";

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const vocabulary = ref<MapReviewTrapVocabulary>({
	species: [],
	sex: [],
	trap_type: [],
	lure: [],
	test_method: [],
	test_target: [],
	test_status: [],
	products: [],
});
const configurations = ref<TrapConfigurationResult[]>([]);
const loading = ref(false);
const saving = ref(false);
const error = ref("");

const catalog = ref<TrapTypeCatalogEntry[]>([]);
const catalogLoading = ref(false);
const savingEnablement = ref<string | null>(null);
const addTrapTypeOpen = ref(false);
const trapTypeSearch = ref("");

/** The page is the district's own short list, not the whole catalog. */
const enabledCatalog = computed(() =>
	catalog.value.filter((entry) => entry.enabled),
);

/** What the add modal offers: catalog types this district does not field yet,
 *  narrowed by a search over the fields a person might recognise one by. */
const availableCatalog = computed(() => {
	const query = trapTypeSearch.value.trim().toLowerCase();
	return catalog.value.filter((entry) => {
		if (entry.enabled || entry.retired) return false;
		if (query === "") return true;
		return [
			entry.name,
			entry.acronym,
			entry.manufacturer,
			entry.model,
			entry.product_code,
			entry.target_species,
			entry.provenance,
		]
			.filter((value): value is string => Boolean(value))
			.some((value) => value.toLowerCase().includes(query));
	});
});

/** A district trap_type row's name is usually the catalog acronym (BGSENT);
 *  look the catalog up by either so both screens label the product the same. */
const catalogByTypeName = computed(() => {
	const byName = new Map<string, TrapTypeCatalogEntry>();
	for (const entry of catalog.value) {
		if (entry.acronym) byName.set(entry.acronym.toLowerCase(), entry);
		byName.set(entry.name.toLowerCase(), entry);
	}
	return byName;
});

function trapTypeLabel(entry: MapReviewVocabularyEntry): string {
	const linked = entry.catalog_trap_type_id
		? catalog.value.find(
				(item) => item.trap_type_id === entry.catalog_trap_type_id,
			)
		: undefined;
	const match = linked ?? catalogByTypeName.value.get(entry.name.toLowerCase());
	if (!match) return entry.name;
	return match.acronym ? `${match.name} (${match.acronym})` : match.name;
}

/** The configuration picker is driven by enablement: only district rows linked
 *  to an enabled catalog type are offered. A configuration being superseded keeps
 *  its current type selectable even if that type has since been disabled, so an
 *  old setup can still be corrected. */
const trapTypeChoices = computed(() => {
	const enabledCatalogIDs = new Set(
		catalog.value
			.filter((entry) => entry.enabled)
			.map((entry) => entry.trap_type_id),
	);
	const choices = vocabulary.value.trap_type.filter(
		(entry) =>
			entry.catalog_trap_type_id !== null &&
			entry.catalog_trap_type_id !== undefined &&
			enabledCatalogIDs.has(entry.catalog_trap_type_id),
	);
	if (
		editingID.value !== null &&
		form.trap_type_id !== 0 &&
		!choices.some((entry) => entry.id === form.trap_type_id)
	) {
		const current = vocabulary.value.trap_type.find(
			(entry) => entry.id === form.trap_type_id,
		);
		if (current) choices.push(current);
	}
	return choices;
});

function openAddTrapType() {
	error.value = "";
	trapTypeSearch.value = "";
	addTrapTypeOpen.value = true;
}

function closeAddTrapType() {
	addTrapTypeOpen.value = false;
}

/** Adds one and leaves the modal open, so several can be added in a row. */
async function addTrapType(entry: TrapTypeCatalogEntry) {
	await toggleEnablement(entry, true);
}

const editingID = ref<number | null>(null);

const form = reactive<TrapConfigurationWrite>({
	name: "",
	trap_type_id: 0,
	lures: [],
	setup_instructions: "",
});

function typeName(id: number): string {
	const entry = vocabulary.value.trap_type.find((item) => item.id === id);
	return entry ? trapTypeLabel(entry) : "—";
}

function lureNames(ids: number[]): string[] {
	return ids.map(
		(id) =>
			vocabulary.value.lure.find((entry) => entry.id === id)?.name ??
			String(id),
	);
}

function resetForm() {
	form.name = "";
	form.trap_type_id = 0;
	form.lures = [];
	form.setup_instructions = "";
	editingID.value = null;
}

function beginSupersede(config: TrapConfigurationResult) {
	editingID.value = config.id;
	form.name = config.name;
	form.trap_type_id = config.trap_type_id;
	form.lures = [...config.lures];
	form.setup_instructions = config.setup_instructions ?? "";
}

async function load() {
	loading.value = true;
	catalogLoading.value = true;
	error.value = "";
	try {
		const [vocab, configs, catalogResp] = await Promise.all([
			apiClient.JSONGet<MapReviewTrapVocabulary>("/api/trap-vocabulary"),
			apiClient.JSONGet<TrapConfigurationResult[]>("/api/trap-configuration"),
			apiClient.JSONGet<TrapTypeCatalogResponse>("/api/trap-type-catalog"),
		]);
		vocabulary.value = vocab;
		configurations.value = configs;
		catalog.value = catalogResp.entries;
	} catch (e: unknown) {
		error.value =
			e instanceof Error ? e.message : "Failed to load configurations.";
	} finally {
		loading.value = false;
		catalogLoading.value = false;
	}
}

async function toggleEnablement(entry: TrapTypeCatalogEntry, enabled: boolean) {
	savingEnablement.value = entry.trap_type_id;
	error.value = "";
	try {
		await apiClient.JSONPost("/api/trap-type-enablement", {
			catalog_trap_type_id: entry.trap_type_id,
			enabled,
		});
		showNotification(
			enabled ? "Trap type enabled" : "Trap type disabled",
			`${entry.name} is now ${enabled ? "enabled" : "disabled"} for field use.`,
		);
		// Enabling ensures a linked district trap_type row; reload so the
		// configuration picker offers it immediately.
		await load();
	} catch (e: unknown) {
		error.value =
			e instanceof Error ? e.message : "Failed to update enablement.";
	} finally {
		savingEnablement.value = null;
	}
}

async function submit() {
	saving.value = true;
	error.value = "";
	const body: TrapConfigurationWrite = {
		name: form.name,
		trap_type_id: form.trap_type_id,
		lures: [...form.lures],
		setup_instructions: form.setup_instructions || null,
	};
	try {
		if (editingID.value === null) {
			await apiClient.JSONPost<TrapConfigurationResult>(
				"/api/trap-configuration",
				body,
			);
			showNotification(
				"Configuration created",
				"The family and its first version are stored.",
			);
		} else {
			await apiClient.JSONPost<TrapConfigurationResult>(
				`/api/trap-configuration/${editingID.value}/supersede`,
				body,
			);
			showNotification(
				"Configuration superseded",
				"A new immutable version is stored.",
			);
		}
		resetForm();
		await load();
	} catch (e: unknown) {
		error.value =
			e instanceof Error ? e.message : "Failed to save the configuration.";
	} finally {
		saving.value = false;
	}
}

async function retire(id: number) {
	error.value = "";
	try {
		await apiClient.JSONPatch(`/api/trap-configuration/${id}`);
		showNotification(
			"Configuration retired",
			"Existing instances are unaffected.",
		);
		await load();
	} catch (e: unknown) {
		error.value =
			e instanceof Error ? e.message : "Failed to retire the configuration.";
	}
}

onMounted(load);
</script>
